import { h, Fragment } from "preact";
import { useState, useRef, useEffect } from "preact/hooks";

import { getCoupon } from "../../../foundation/api";
import { Coupon } from "../../../foundation/api/model";
import { BillingPeriod, CouponStatus, Keyboard } from "../../../foundation/enums";
import classname from "../../../foundation/utils/classname";
import { getCouponStatus } from "../../../foundation/utils/payment";
import IconSpinner from "../../Icons/IconSpinner";
import Button from "../../v2/Button";
import Input from "../../v2/Input";

import styles from "./CouponContainer.module.css";

function getCouponInvalidMessage(couponStatus: CouponStatus, period: BillingPeriod, couponId: string): JSX.Element {
    const letUsKnow = (
        <p>
            If you believe this is a mistake, let us know at <a href={`mailto:support@zeplin.io?subject=Unable to apply coupon code: ${couponId}`}>support@zeplin.io</a>
        </p>
    );

    if (couponStatus === CouponStatus.NOT_FOUND) {
        return (
            <p>It looks like the coupon code <b>{couponId}</b> doesn't exist. {letUsKnow}</p>
        );
    }

    if (couponStatus === CouponStatus.EXPIRED) {
        return (
            <p>It looks like the coupon code <b>{couponId}</b> has expired. {letUsKnow}</p>
        );
    }

    if (couponStatus === CouponStatus.MAXED_OUT) {
        return (
            <p>It looks like the coupon code <b>{couponId}</b> has been used many times. {letUsKnow}</p>
        );
    }

    return (
        <p>It looks like the coupon code <b>{couponId}</b> doesn't exist. {letUsKnow}</p>
    );
}

interface Props {
    couponIdToApply?: string;
    appliedCoupon?: Coupon;
    setAppliedCoupon: (coupon: Coupon | undefined) => void;
    loadingState: null | "Tax" | "Coupon";
    setLoadingState: (loadingState: "Coupon" | null) => void;
}

export default function CouponContainer({
    couponIdToApply,
    appliedCoupon,
    setAppliedCoupon,
    loadingState,
    setLoadingState
}: Props) {
    const [couponAreaActive, setCouponAreaActive] = useState(false);
    const [couponId, setCouponId] = useState(couponIdToApply ?? "");
    const [couponErrorMessage, setCouponErrorMessage] = useState<JSX.Element | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (couponAreaActive) {
            inputRef.current.focus();
        }
    }, [couponAreaActive]);

    useEffect(() => {
        if (couponIdToApply) {
            setCouponAreaActive(true);
            onApplyClicked();
        }
    }, []);

    function handleKeyDown(event: KeyboardEvent) {
        if (event.key === Keyboard.Key.Escape) {
            setCouponAreaActive(false);
            setCouponId("");
            setAppliedCoupon(undefined);
        }
    }

    function onEnableCouponArea() {
        setCouponAreaActive(true);
    }

    function onCouponInputChange(event: Event) {
        const { value } = event.target as HTMLInputElement;
        setCouponId(value);
    }

    function onApplyClicked() {
        setAppliedCoupon(undefined);
        setCouponErrorMessage(null);
        setLoadingState("Coupon");
        getCoupon(couponId).then(couponRes => {
            const couponStatus = getCouponStatus({ coupon: couponRes, period: BillingPeriod.MONTHLY });

            if (couponStatus === CouponStatus.SUCCESS) {
                setAppliedCoupon(couponRes);
                setCouponErrorMessage(null);
                mixpanel.master.track("Applied coupon", {
                    type: "manual",
                    source: couponRes.metadata?.source
                });
            } else {
                setCouponErrorMessage(
                    getCouponInvalidMessage(couponStatus, BillingPeriod.MONTHLY, couponRes ? couponRes.id : couponId)
                );
            }
        }).catch(error => {
            setCouponErrorMessage(
                <p>{error.title}</p>
            );
            setAppliedCoupon(undefined);
        }).finally(() => setLoadingState(null));
    }

    return (
        <div class={classname("body-1", styles.couponContainer)}>
            {couponAreaActive
                ? (
                    <>
                        <div class="caption-2">Enter coupon code</div>
                        <div class={styles.couponInput}>
                            <Input
                                inputRef={inputRef}
                                placeholder="Enter code"
                                disabled={loadingState === "Coupon"}
                                value={couponId}
                                onKeyDown={handleKeyDown}
                                onChange={onCouponInputChange}/>
                            <Button
                                disabled={couponId === appliedCoupon?.id || loadingState === "Coupon"}
                                onClick={onApplyClicked}>
                                {loadingState === "Coupon" && <IconSpinner/>}
                                {loadingState !== "Coupon" && couponId === appliedCoupon?.id ? "Applied" : "Apply"}
                            </Button>
                        </div>
                        {couponErrorMessage && <div className={styles.couponMessage}>{couponErrorMessage}</div>}
                    </>
                )
                : <div>Have a <span className={styles.clickable} onClick={onEnableCouponArea}>coupon code</span>?</div>
            }
        </div>
    );
}
